<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import type { FooterArticleBlock } from '../model/types';

  interface FooterArticleProps {
    content: FooterArticleBlock[] | string;
  }

  const props = defineProps<FooterArticleProps>();

  const html = computed(() => (typeof props.content === 'string' ? props.content : ''));
  const blocks = computed<FooterArticleBlock[]>(() => (typeof props.content === 'string' ? [] : props.content));
</script>

<template>
  <div
    class="footer-article"
    data-testid="footer-article"
  >
    <!-- eslint-disable vue/no-v-html -- content из доверенного бэкенд-источника (SEO), не пользовательский ввод -->
    <div
      v-if="html"
      class="footer-article__html"
      data-testid="footer-article-html"
      v-html="html"
    />
    <!-- eslint-enable vue/no-v-html -->

    <template v-for="(block, index) in blocks">
      <Typo
        v-if="block.type === 'heading'"
        :key="`h-${index}`"
        class="footer-article__heading"
        :variant="TypoVariant.Heading5"
        tag="h2"
        data-testid="footer-article-heading"
      >
        {{ block.text }}
      </Typo>

      <Typo
        v-else-if="block.type === 'description'"
        :key="`p-${index}`"
        class="footer-article__text"
        :variant="TypoVariant.BodyLRegular"
        tag="p"
        data-testid="footer-article-text"
      >
        {{ block.text }}
      </Typo>

      <ul
        v-else
        :key="`l-${index}`"
        class="footer-article__list"
        data-testid="footer-article-list"
      >
        <li
          v-for="(item, itemIndex) in block.items"
          :key="itemIndex"
          class="footer-article__list-item"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ item }}
          </Typo>
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .footer-article {
    color: var(--color-text-primary);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);

    &__heading {
      color: var(--color-text-primary);
      margin: 0;

      &:not(:first-child) {
        margin-top: var(--spacing-m);
      }
    }

    &__text {
      color: var(--color-text-primary);
      margin: 0;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      margin: 0;
      padding-left: var(--spacing-l);
    }

    &__list-item {
      color: var(--color-text-primary);
      list-style: disc;
    }

    &__html {
      color: var(--color-text-primary);
      display: flex;
      flex-direction: column;
      font-family: 'GT Eesti Pro', sans-serif;
      gap: var(--spacing-s);

      ::v-deep(h1),
      ::v-deep(h2),
      ::v-deep(h3) {
        margin: 0;

        &:not(:first-child) {
          margin-top: var(--spacing-m);
        }
      }

      ::v-deep(p) {
        margin: 0;
      }

      ::v-deep(ul),
      ::v-deep(ol) {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-s);
        margin: 0;
        padding-left: var(--spacing-l);
      }

      ::v-deep(li) {
        list-style: disc;
      }
    }
  }
</style>
